import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import OnboardingOverlay from "@/components/OnboardingOverlay";
import {
  LayoutDashboard, Users, Clock, CheckSquare, CalendarDays, LineChart, UserCog,
  Target, Settings as SettingsIcon, LogOut, X, Sparkles, CreditCard, AlertCircle,
  ShoppingCart, Package, BarChart3, Banknote, Wallet, ClipboardList, ChefHat,
  ShieldCheck, Receipt, ShoppingBag, Gauge, PackageSearch, FlaskConical, Calculator,
  Search, Boxes, Factory, Printer, Activity, Recycle, BrainCircuit, LifeBuoy,
  AlertTriangle, MoreHorizontal, PanelRightClose, PanelRightOpen, Menu as MenuIcon,
  Infinity as InfinityIcon, UtensilsCrossed, Truck, PiggyBank, Tags, Globe,
  ArrowLeft, Sun, MessageCircleQuestion, ClipboardCheck, Rocket,
} from "lucide-react";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import BranchSwitcher from "@/components/BranchSwitcher";
import QuickSwitcher from "@/components/QuickSwitcher";
import OperatorSwitcher from "@/components/OperatorSwitcher";
import CommandPalette, { useGlobalHotkeys } from "@/components/CommandPalette";
import { api } from "@/lib/api";
import { OplyxLogo } from "@/components/brand/OplyxLogo";
import ErrorBoundary from "@/components/ErrorBoundary";
import InsightsWidget from "@/components/InsightsWidget";
// Chat assistant desativado (obsoleto) — componente mantido em
// @/components/ChatAssistant para eventual reativação com nova função.

// ── Nav items (unchanged from previous layout) ───────────────────────────────
const NAV_SECTIONS = [
  {
    id: "main", label: "Visão geral",
    items: [
      { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true, keywords: "home inicio painel", roles: ["owner","manager","staff","accountant","financial","purchasing"] },
      { to: "/live-ops", icon: Activity, label: "Situação da Operação", roles: ["owner"], requiresPermission: "can_view_live_ops" },
      { to: "/goals", icon: Target, label: "Metas", roles: ["owner","manager","accountant","financial"] },
    ],
  },
  {
    id: "sales", label: "Vendas",
    items: [
      { to: "/pos", icon: ShoppingCart, label: "Caixa (PDV)", keywords: "vender venda pos pdv caixa", roles: ["owner","manager","staff"] },
      { to: "/tabs", icon: ClipboardList, label: "Comandas", keywords: "comanda mesa", roles: ["owner","manager","staff"] },
      { to: "/tabs/cancelled", icon: X, label: "Canceladas", roles: ["owner","manager"] },
      { to: "/kds", icon: ChefHat, label: "Cozinha (KDS)", keywords: "cozinha pedidos", roles: ["owner","manager","staff"], requiresFeature: "kds" },
      { to: "/waitlist", icon: Clock, label: "Fila de espera", roles: ["owner","manager","staff"] },
      // Era "Fiscal (NFC-e)". A tela cobre NFC-e, NF-e, devolução e inutilização
      // — e depois do cutover para o emissor próprio o parêntese só confundiria.
      { to: "/fiscal", icon: Receipt, label: "Fiscal", keywords: "nota fiscal nfce nfe", roles: ["owner","manager","accountant","financial"] },
      { to: "/nfe", icon: Receipt, label: "Emitir NF-e (B2B)", roles: ["owner","manager","accountant","financial","staff"] },
      { to: "/tips", icon: Banknote, label: "Gorjetas", keywords: "gorjeta taxa servico", roles: ["owner","manager","staff","accountant","financial"] },
      { to: "/owner-consumption", icon: UtensilsCrossed, label: "Consumo interno", roles: ["owner","manager","staff"] },
      { to: "/delivery", icon: Truck, label: "Delivery", roles: ["owner","manager","staff"], requiresFeature: "delivery" },
      // Sem requiresFeature "delivery": quem vende só pelo iFood também precisa
      // calcular o preço com a taxa da plataforma.
      { to: "/delivery-pricing", icon: Tags, label: "Preços no delivery", roles: ["owner","manager"], fullOnly: "intelligence" },
    ],
  },
  {
    id: "financial", label: "Financeiro",
    items: [
      { to: "/financial-central", icon: Gauge, label: "Central Financeira", keywords: "caixa financeiro", roles: ["owner","manager","accountant","financial"] },
      { to: "/cash-flow", icon: Banknote, label: "Fluxo de caixa", keywords: "fluxo caixa saldo projecao previsao entradas saidas", roles: ["owner","manager","accountant","financial"] },
      { to: "/reserves", icon: PiggyBank, label: "Reservas", roles: ["owner","manager"], fullOnly: "intelligence" },
      { to: "/cash-reports", icon: BarChart3, label: "Relatórios de caixa", keywords: "caixa pos cartao taxa divergencia operador", roles: ["owner","manager","accountant","financial"] },
      { to: "/financial-central?tab=payables", icon: LineChart, label: "Contas a pagar", keywords: "boleto pagamento fornecedor", roles: ["owner","accountant","financial","manager"] },
      { to: "/taxes", icon: Receipt, label: "Impostos", keywords: "imposto simples das guia pgdas tributo aliquota rbt12", roles: ["owner","manager","accountant","financial"] },
      { to: "/payroll-charges", icon: Wallet, label: "Encargos da folha", keywords: "inss fgts encargo folha", roles: ["owner","manager","accountant","financial"] },
      { to: "/opcosts", icon: Wallet, label: "Despesas operacionais", keywords: "folha aluguel despesa operacional custo", roles: ["owner","manager","accountant","financial"] },
      { to: "/purchases", icon: ShoppingBag, label: "Compras (NF-e)", keywords: "xml nfe entrada nota compra", roles: ["owner","manager","accountant","purchasing","financial"], requiresPermission: "can_manage_stock" },
      { to: "/dre", icon: Receipt, label: "DRE · Resultado", keywords: "dre lucro prejuizo resultado", roles: ["owner","manager","accountant","financial"] },
    ],
  },
  {
    id: "products", label: "Estoque",
    items: [
      { to: "/replenishment", icon: PackageSearch, label: "Reposição", keywords: "reabastecimento estoque lista compra pedido", roles: ["owner","manager","accountant","purchasing","financial","staff"] },
      { to: "/products", icon: Package, label: "Produtos", keywords: "cardapio produto", roles: ["owner","manager"] },
      { to: "/ingredients", icon: FlaskConical, label: "Ingredientes", roles: ["owner","manager","purchasing"] },
      { to: "/consumption", icon: Recycle, label: "Consumo automático", roles: ["owner","manager","purchasing","stockist"] },
      { to: "/estoque", icon: Boxes, label: "Estoque", keywords: "estoque saldo movimentacao insumo", roles: ["owner","manager","purchasing","accountant"], requiresPermission: "can_manage_stock" },
      { to: "/producao", icon: Factory, label: "Produção", keywords: "producao lote ficha", roles: ["owner","manager","purchasing","staff"] },
      { to: "/cost-analytics", icon: Calculator, label: "Custos (CMV & CMO)", keywords: "cmv cmo custo", roles: ["owner","manager","accountant","purchasing"] },
    ],
  },
  {
    id: "customers", label: "Clientes",
    items: [
      { to: "/customers", icon: Users, label: "Clientes", keywords: "cliente cashback crm", roles: ["owner","manager","staff"] },
      { to: "/reactivate", icon: Sparkles, label: "Reativação", roles: ["owner","manager"] },
    ],
  },
  {
    id: "reports", label: "Relatórios",
    items: [
      // "Análise operacional" foi absorvida por "Relatórios gerais" — horários,
      // dias da semana e comandas agora dividem o mesmo filtro de período/filial.
      { to: "/reports", icon: BarChart3, label: "Relatórios gerais", keywords: "analise relatorio ticket horario dia", roles: ["owner","manager","accountant","financial"] },
      { to: "/sales-report", icon: Receipt, label: "Relatório de vendas", roles: ["owner","manager","accountant","financial"] },
      { to: "/accounting", icon: Calculator, label: "Contabilidade", roles: ["owner","manager","accountant","financial"] },
      // DRE fica só em Financeiro no menu (duplicar aqui faria a aba Financeiro
      // acender ao abrir pelo atalho). O acesso a partir daqui é pelo cross-link
      // "Ver DRE do período" dentro de Relatórios gerais, que leva o filtro junto.
    ],
  },
  {
    id: "intelligence", label: "Oplyx Intelligence",
    items: [
      // Só análise. Reservas foi para Financeiro, Preços no delivery para Vendas
      // e Valor gerado para Configurações — ferramentas, não inteligência.
      // Hoje primeiro: o grupo abre nele (primeiro item visível). Caixa é o Radar
      // de Caixa — aberto também a financeiro/contador, que não veem o Hoje.
      { to: "/intelligence/today", icon: Sun, label: "Hoje", roles: ["owner","manager"], fullOnly: "intelligence" },
      { to: "/intelligence/cash", icon: Wallet, label: "Caixa", roles: ["owner","manager","accountant","financial"], fullOnly: "intelligence" },
      { to: "/intelligence/ask", icon: MessageCircleQuestion, label: "Pergunte", roles: ["owner","manager"], fullOnly: "intelligence" },
      { to: "/intelligence/results", icon: ClipboardCheck, label: "Resultados", roles: ["owner","manager"], fullOnly: "intelligence" },
      { to: "/insights", icon: Sparkles, label: "Insights", roles: ["owner","manager"], fullOnly: "intelligence" },
      { to: "/forecast", icon: BrainCircuit, label: "Planejar produção", roles: ["owner","manager"], fullOnly: "intelligence" },
      { to: "/menu-analysis", icon: ShoppingBag, label: "Análise de cardápio", roles: ["owner","manager"], fullOnly: "intelligence" },
      { to: "/dre-projection", icon: BarChart3, label: "DRE projetada", roles: ["owner","manager","accountant","financial"], fullOnly: "intelligence" },
    ],
  },
  {
    id: "team", label: "Equipe",
    items: [
      { to: "/team", icon: UserCog, label: "Equipe", keywords: "funcionario colaborador", roles: ["owner","manager"] },
      { to: "/attendance", icon: Clock, label: "Ponto Eletrônico", roles: ["owner","manager"], requiresFeature: "attendance" },
      { to: "/attendance/my-clock", icon: Clock, label: "Meu ponto", roles: ["owner","manager","staff","financial","accountant","purchasing"], requiresFeature: "attendance" },
      { to: "/tasks", icon: CheckSquare, label: "Tarefas", roles: ["owner","manager","staff"] },
      { to: "/calendar", icon: CalendarDays, label: "Agenda", roles: ["owner","manager","staff","financial","purchasing"] },
    ],
  },
  {
    id: "settings", label: "Configurações",
    items: [
      { to: "/implantacao", icon: Rocket, label: "Implantação", roles: ["owner","manager"] },
      { to: "/billing", icon: CreditCard, label: "Assinatura & plano", keywords: "plano pagamento assinatura", roles: ["owner"] },
      { to: "/value-report", icon: ShieldCheck, label: "Valor gerado", roles: ["owner","manager"], fullOnly: "intelligence" },
      { to: "/settings", icon: SettingsIcon, label: "Ajustes gerais", keywords: "configuracao configuracoes ajustes", roles: ["owner"] },
      { to: "/settings/public-profile", icon: Globe, label: "Perfil público", roles: ["owner"] },
      { to: "/settings/printers", icon: Printer, label: "Impressoras", keywords: "impressora cupom", roles: ["owner", "manager"] },
      { to: "/fiscal/configuracao", icon: ShieldCheck, label: "Configuração fiscal", roles: ["owner"], danger: true },
    ],
  },
  {
    id: "support", label: "Ajuda",
    items: [
      { to: "/support", icon: LifeBuoy, label: "Suporte", roles: ["owner","manager","staff","accountant","financial","purchasing"] },
    ],
  },
];

// ── Deck groups (top-level tabs) ─────────────────────────────────────────────
const GROUPS = [
  { id: "inicio",       label: "Início",       icon: LayoutDashboard, sectionIds: ["main"] },
  { id: "vendas",       label: "Vendas",        icon: ShoppingCart,    sectionIds: ["sales"] },
  { id: "financeiro",   label: "Financeiro",    icon: Gauge,           sectionIds: ["financial"] },
  { id: "estoque",      label: "Estoque",       icon: Package,         sectionIds: ["products"] },
  { id: "clientes",     label: "Clientes",      icon: Users,           sectionIds: ["customers"] },
  { id: "relatorios",   label: "Relatórios",    icon: BarChart3,       sectionIds: ["reports"] },
  { id: "intelligence", label: "Intelligence",  icon: BrainCircuit,    sectionIds: ["intelligence"], isIntel: true },
  { id: "equipe",       label: "Equipe",        icon: UserCog,         sectionIds: ["team"] },
  { id: "config",       label: "Config.",       icon: SettingsIcon,    sectionIds: ["settings", "support"], isOverflow: true },
];

// Groups shown in the mobile bottom bar. O drawer "Mais" lista todos os grupos:
// no celular a faixa de abas de cada grupo rola de lado e escondia telas
// (Impostos era a 6ª aba de Financeiro, fora da tela).
const MOBILE_PRIMARY_IDS = ["inicio", "vendas", "financeiro", "intelligence"];

// Item de plano Full visto por quem não tem o recurso: continua no menu (upsell);
// o clique abre a tela e o 403 do backend dispara o UpgradePrompt.
function FullBadge() {
  return (
    <span className="ml-1 rounded px-1 py-px text-[9px] font-bold uppercase tracking-wide bg-[#00FF88]/15 text-[#00A855] dark:text-[#00FF88] flex-shrink-0">
      Full
    </span>
  );
}

// Rotas do Intelligence — de onde um insight pode mandar o usuário para outra tela.
const INTEL_PATHS = ["/intelligence", "/insights", "/forecast", "/menu-analysis", "/dre-projection"];

export default function AppShell() {
  const [sub, setSub] = useState(null);
  const [logo, setLogo] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [onboarding, setOnboarding] = useState(null);
  const [featuresMeta, setFeaturesMeta] = useState(null);
  const [certAlert, setCertAlert] = useState(null);
  const [supportWhatsapp, setSupportWhatsapp] = useState("5548991207631");
  const [posStandalone, setPosStandalone] = useState(false);
  const [intelOpen, setIntelOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [, forceUserRefresh] = useState(0);

  const mainScrollRef = useRef(null);
  const overflowRef = useRef(null);
  useGlobalHotkeys(setPaletteOpen);
  const nav = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("oplyx_user") || "{}");
  const role = user.role || "owner";
  const perms = user.permissions || {};
  const logout = () => { localStorage.clear(); nav("/auth"); };

  // Role/permissões podem ter sido alteradas pelo owner/manager em outra sessão
  // (ex.: Equipe > Permissões) enquanto este usuário já estava logado — sem isso,
  // o menu (e os gates de UI que leem localStorage) só refletiriam a mudança
  // depois de um logout/login manual.
  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      const cached = JSON.parse(localStorage.getItem("oplyx_user") || "{}");
      if (JSON.stringify(data.permissions || {}) !== JSON.stringify(cached.permissions || {})
          || data.role !== cached.role) {
        localStorage.setItem("oplyx_user", JSON.stringify(data));
        forceUserRefresh(v => v + 1);
      }
    }).catch(() => {});
  }, []);
  const loadLogo = (force = false) => {
    const KEY = "oplyx_biz_v1";
    const TTL = 5 * 60 * 1000;
    if (!force) {
      try {
        const cached = JSON.parse(sessionStorage.getItem(KEY) || "null");
        if (cached && Date.now() - cached.ts < TTL) { setLogo(cached.logo_url || null); return; }
      } catch (_) {}
    }
    api.get("/business/info").then(r => {
      setLogo(r.data.logo_url || null);
      try { sessionStorage.setItem(KEY, JSON.stringify({ logo_url: r.data.logo_url || null, ts: Date.now() })); } catch (_) {}
    }).catch(() => {});
  };

  useEffect(() => {
    const TTL = 5 * 60 * 1000;

    const cachedFetch = (key, apiFn, setter) => {
      try {
        const cached = JSON.parse(sessionStorage.getItem(key) || "null");
        if (cached && Date.now() - cached.ts < TTL) { setter(cached.data); return; }
      } catch (_) {}
      apiFn().then(r => {
        setter(r.data);
        try { sessionStorage.setItem(key, JSON.stringify({ data: r.data, ts: Date.now() })); } catch (_) {}
      }).catch(() => {});
    };

    cachedFetch("oplyx_sub_v1", () => api.get("/subscription"), setSub);
    cachedFetch("oplyx_feat_v1", () => api.get("/features/me"), setFeaturesMeta);
    fetch("/api/public/platform-config").then(r => r.json()).then(d => { if (d.support_whatsapp) setSupportWhatsapp(d.support_whatsapp); }).catch(() => {});
    loadLogo();

    const ROLE_NEEDS_ONBOARDING = ["owner", "manager"];
    if (ROLE_NEEDS_ONBOARDING.includes(user.role)) {
      api.get("/onboarding/status").then(r => {
        setOnboarding(r.data);
        if (!r.data?.completed && !sessionStorage.getItem("onboarding_overlay_shown")) setOverlayOpen(true);
      }).catch(() => {});
    }
    if (["owner", "manager", "accountant", "financial"].includes(user.role)) {
      const CERT_KEY = "oplyx_cert_v1";
      try {
        const cached = JSON.parse(sessionStorage.getItem(CERT_KEY) || "null");
        if (cached && Date.now() - cached.ts < TTL) {
          const s = cached.data?.status;
          if (s === "expiring_soon" || s === "expired") setCertAlert(cached.data);
        } else {
          api.get("/fiscal/certificates/status").then(r => {
            const s = r.data?.status;
            if (s === "expiring_soon" || s === "expired") setCertAlert(r.data);
            try { sessionStorage.setItem(CERT_KEY, JSON.stringify({ data: r.data, ts: Date.now() })); } catch (_) {}
          }).catch(() => {});
        }
      } catch (_) {}
    }
    const h = () => loadLogo(true);
    window.addEventListener("logo-updated", h);
    return () => window.removeEventListener("logo-updated", h);
  }, []);

  // POS standalone detection
  useEffect(() => {
    const isPosRoute = location.pathname === "/pos" || location.pathname.startsWith("/pos?");
    if (!isPosRoute) { setPosStandalone(false); return; }
    const mq = window.matchMedia("(display-mode: standalone)");
    const update = () => setPosStandalone(isPosRoute && (mq.matches || window.navigator.standalone === true));
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else if (mq.addListener) mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else if (mq.removeListener) mq.removeListener(update);
    };
  }, [location.pathname]);

  // Scroll to top on route change
  useEffect(() => {
    mainScrollRef.current?.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  // Close overflow on outside click — but not when user is interacting with a dialog portal
  useEffect(() => {
    const handler = (e) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target)) {
        if (!e.target.closest('[role="dialog"]')) setOverflowOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close mobile drawer on route change
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  // Aba ativa da faixa de sub-navegação sempre à vista (no celular ela pode estar fora da tela).
  useEffect(() => {
    const el = document.querySelector('[data-subnav] [aria-current="page"]');
    el?.scrollIntoView?.({ inline: "center", block: "nearest" });
  }, [location.pathname, location.search]);

  // Última tela antes do Suporte — vai no contexto do chamado ("onde estava").
  useEffect(() => {
    if (location.pathname.startsWith("/support")) return;
    try { sessionStorage.setItem("oplyx_last_route", location.pathname); } catch { /* opcional */ }
  }, [location.pathname]);

  const showTrialBanner = sub && sub.status === "trialing" && sub.days_left <= 7 && user.role === "owner";
  const hasIntel = featuresMeta?.features?.intelligence ?? false;
  // Só depois de carregar as features: evita o selo piscar para quem é Full.
  const lockedFull = (it) => !!(it.fullOnly && featuresMeta?.features && !featuresMeta.features[it.fullOnly]);

  // Insight que manda para outra tela (Metas, Relatório de vendas…) leva
  // `state.fromIntel`; a tela de destino ganha um atalho de volta.
  const fromIntel = location.state?.fromIntel;
  const showBackToIntel = !!fromIntel && !INTEL_PATHS.some(p => location.pathname.startsWith(p));

  // NAV_SECTIONS with dynamic Reimpressão item in sales
  const allSections = useMemo(() => NAV_SECTIONS.map(s => {
    if (s.id !== "sales") return s;
    return { ...s, items: [...s.items, { to: "/reimpressao", icon: Printer, label: "Reimpressão", roles: ["owner","manager","staff"] }] };
  }), []);

  const isItemAllowed = useCallback((it) => {
    if (it.requiresFeature && featuresMeta?.features) {
      if (!featuresMeta.features[it.requiresFeature]) return false;
    }
    if (!it.roles.includes(role)) {
      if (it.requiresPermission && perms[it.requiresPermission]) return true;
      return false;
    }
    return true;
  }, [featuresMeta, role, perms]);

  const visibleGroups = useMemo(() => GROUPS.map(group => {
    const secs = allSections.filter(s => group.sectionIds.includes(s.id));
    const visibleItems = secs.flatMap(sec => sec.items.filter(isItemAllowed));
    return { ...group, visibleItems };
  }).filter(g => g.visibleItems.length > 0), [allSections, isItemAllowed]);

  const activeGroupId = useMemo(() => {
    for (const group of visibleGroups) {
      for (const item of group.visibleItems) {
        const itemPath = item.to.split("?")[0];
        if (itemPath === "/") {
          if (item.end && location.pathname === "/") return group.id;
          continue;
        }
        if (!item.end && location.pathname.startsWith(itemPath)) return group.id;
        if (item.end && location.pathname === itemPath) return group.id;
      }
    }
    if (location.pathname === "/") return "inicio";
    return visibleGroups[0]?.id ?? "inicio";
  }, [location.pathname, visibleGroups]);

  const activeSubItems = useMemo(() => {
    const group = visibleGroups.find(g => g.id === activeGroupId);
    return group?.visibleItems ?? [];
  }, [activeGroupId, visibleGroups]);

  if (user.is_platform_admin) return <Navigate to="/admin" replace />;

  // ── Desktop top nav ─────────────────────────────────────────────────────────
  const DeckTopBar = () => {
    const mainGroups = visibleGroups.filter(g => !g.isOverflow);
    const overflowGroup = visibleGroups.find(g => g.isOverflow);

    return (
      <div className="hidden lg:flex h-12 items-stretch bg-white dark:bg-[#0B0D0F] border-b border-neutral-200 dark:border-white/[0.06] flex-shrink-0 relative z-20">
        {/* Logo */}
        <div className="flex items-center gap-2 px-4 border-r border-neutral-200 dark:border-white/[0.06] flex-shrink-0 min-w-0">
          {logo
            ? <img src={logo} alt="logo" className="w-5 h-5 object-contain rounded flex-shrink-0" data-testid="shell-logo" />
            : <OplyxLogo size={18} wordmark={false} tone="adaptive" />}
          <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate max-w-[140px]">
            {user.business_name || "OPLYX"}
          </span>
        </div>

        {/* Nav tabs */}
        <nav className="flex items-stretch flex-1 overflow-x-auto" style={{ scrollbarWidth: "none" }} data-testid="main-nav">
          {mainGroups.map(group => {
            const isActive = activeGroupId === group.id;
            const Icon = group.icon;
            const firstItem = group.visibleItems[0];
            return (
              <NavLink
                key={group.id}
                to={firstItem?.to ?? "/"}
                data-testid={`nav-group-${group.id}`}
                className={[
                  "flex items-center gap-1.5 px-3 text-sm font-medium border-b-2 flex-shrink-0 transition-colors whitespace-nowrap",
                  group.isIntel
                    ? isActive
                      ? "border-[#00FF88] text-[#00A855] dark:text-[#00FF88] bg-[#00FF88]/[0.04]"
                      : "border-transparent text-[#00A855] dark:text-[#00CC6E] hover:bg-[#00FF88]/[0.04]"
                    : isActive
                      ? "border-neutral-900 dark:border-[#00FF88] text-neutral-900 dark:text-neutral-100"
                      : "border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-white/[0.03]",
                ].join(" ")}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.75} />
                {group.label}
                {group.isIntel && (
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] flex-shrink-0" />
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Right side actions */}
        <div className="flex items-center gap-1.5 px-3 border-l border-neutral-200 dark:border-white/[0.06] flex-shrink-0">
          {/* Onboarding progress */}
          {onboarding && !onboarding.completed && (
            <button
              type="button"
              onClick={() => setOverlayOpen(true)}
              data-testid="shell-onboarding-badge"
              className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-primary/30 bg-primary-muted/20 text-xs text-primary-muted-foreground hover:bg-primary-muted/40 transition-colors"
            >
              <Sparkles className="w-3 h-3" strokeWidth={2} />
              <span className="font-medium hidden xl:inline">Setup</span>
              <span className="font-mono text-[10px]">{onboarding.completion_pct || 0}%</span>
            </button>
          )}

          {/* ⌘K search */}
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            data-testid="cmdk-trigger"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-neutral-200 dark:border-white/[0.06] bg-neutral-50 dark:bg-white/[0.03] text-xs text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/[0.06] transition-colors"
          >
            <Search className="w-3.5 h-3.5" />
            <span className="hidden xl:inline">Buscar</span>
            <kbd className="text-[10px] font-mono bg-white dark:bg-white/[0.05] border dark:border-white/[0.06] rounded px-1 py-0.5">⌘K</kbd>
          </button>

          {/* Intelligence panel toggle */}
          <button
            type="button"
            onClick={() => setIntelOpen(v => !v)}
            title={intelOpen ? "Fechar painel Intelligence" : "Abrir painel Intelligence"}
            className="flex items-center justify-center w-8 h-8 rounded-lg border border-neutral-200 dark:border-white/[0.06] hover:bg-neutral-100 dark:hover:bg-white/[0.04] transition-colors"
          >
            {intelOpen
              ? <PanelRightClose className="w-4 h-4 text-neutral-400 dark:text-neutral-500" />
              : <PanelRightOpen className="w-4 h-4 text-[#00A855] dark:text-[#00FF88]" />}
          </button>

          {/* ··· overflow (Config + Filial + Sair) */}
          <div ref={overflowRef} className="relative">
            <button
              type="button"
              onClick={() => setOverflowOpen(v => !v)}
              className="flex items-center justify-center w-8 h-8 rounded-lg border border-neutral-200 dark:border-white/[0.06] hover:bg-neutral-100 dark:hover:bg-white/[0.04] transition-colors"
              data-testid="nav-overflow-btn"
            >
              <MoreHorizontal className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
            </button>

            {overflowOpen && (
              <div className="absolute top-full right-0 mt-1.5 w-56 bg-white dark:bg-[#141618] border border-neutral-200 dark:border-white/[0.06] rounded-xl shadow-xl z-50 overflow-hidden">
                {/* Config nav items */}
                {overflowGroup && (
                  <div className="p-1.5">
                    {overflowGroup.visibleItems.map(item => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setOverflowOpen(false)}
                        data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isActive
                              ? "bg-neutral-900 text-white dark:bg-[#00FF88] dark:text-[#0B0D0F]"
                              : item.danger
                                ? "text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                                : "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/[0.04] hover:text-neutral-900 dark:hover:text-neutral-100"
                          }`
                        }
                      >
                        {item.danger
                          ? <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" strokeWidth={1.5} />
                          : <item.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />}
                        <span>{item.label}</span>
                        {lockedFull(item) && <FullBadge />}
                      </NavLink>
                    ))}
                  </div>
                )}

                {/* Filial */}
                <div className="border-t border-neutral-100 dark:border-white/[0.06]">
                  <BranchSwitcher />
                </div>

                {/* QuickSwitcher + Logout */}
                <div className="border-t border-neutral-100 dark:border-white/[0.06] p-1.5">
                  <div className="px-3 py-1.5 text-xs text-neutral-500 dark:text-neutral-400 font-medium truncate">{user.email}</div>
                  <OperatorSwitcher />
                  <QuickSwitcher />
                  <button
                    data-testid="logout-btn"
                    onClick={logout}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/[0.04] w-full transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sair
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* User avatar */}
          <div className="w-7 h-7 rounded-full bg-neutral-900 dark:bg-white/[0.1] flex items-center justify-center text-white dark:text-neutral-100 text-xs font-bold flex-shrink-0">
            {(user.name || user.email || "U").charAt(0).toUpperCase()}
          </div>
        </div>
      </div>
    );
  };

  // ── Desktop secondary sub-nav ────────────────────────────────────────────────
  const DeckSubNav = () => {
    if (activeSubItems.length <= 1) return null;
    return (
      <div className="relative flex-shrink-0">
      <div
        data-subnav
        className="flex h-9 items-center px-4 pr-10 lg:pr-4 gap-1 bg-white dark:bg-[#0B0D0F] border-b border-neutral-200 dark:border-white/[0.06] overflow-x-auto"
        style={{ scrollbarWidth: "none" }}
      >
        {activeSubItems.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`subnav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                isActive
                  ? "bg-neutral-900 dark:bg-[#00FF88] text-white dark:text-[#0B0D0F]"
                  : item.danger
                    ? "text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                    : "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/[0.04] hover:text-neutral-900 dark:hover:text-neutral-100"
              }`
            }
          >
            {item.danger
              ? <AlertTriangle className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} />
              : <item.icon className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} />}
            {item.label}
            {lockedFull(item) && <FullBadge />}
          </NavLink>
        ))}
      </div>
      {/* No celular a faixa rola de lado: o degradê mostra que há mais abas. */}
      <div className="lg:hidden pointer-events-none absolute right-0 top-0 bottom-px w-10 bg-gradient-to-l from-white dark:from-[#0B0D0F]" />
      </div>
    );
  };

  // ── Intelligence panel (desktop right side) ──────────────────────────────────
  const IntelPanel = () => (
    <div className="hidden lg:flex w-56 xl:w-60 flex-col border-l border-neutral-200 dark:border-white/[0.06] bg-white dark:bg-[#0B0D0F] flex-shrink-0 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100 dark:border-white/[0.06] flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <BrainCircuit className="w-3.5 h-3.5 text-[#00FF88]" strokeWidth={1.75} />
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#00A855] dark:text-[#00FF88]">
            Intelligence
          </span>
        </div>
        <button
          type="button"
          onClick={() => setIntelOpen(false)}
          className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"
          title="Fechar painel"
        >
          <PanelRightClose className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-auto">
        {!hasIntel ? (
          <div className="p-5 text-center">
            <BrainCircuit className="w-8 h-8 text-neutral-300 dark:text-neutral-600 mx-auto mb-2" strokeWidth={1.5} />
            <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed mb-3">
              Oplyx Intelligence disponível no plano Full.
            </p>
            <button
              onClick={() => nav("/billing")}
              className="text-xs font-semibold text-[#00A855] dark:text-[#00FF88] hover:underline"
            >
              Ver planos →
            </button>
          </div>
        ) : (
          // O painel mostra o que importa AGORA — antes repetia o menu do grupo,
          // que já está na barra de cima.
          <div className="p-3">
            <InsightsWidget compact />
          </div>
        )}
      </div>

      {hasIntel && (
        <div className="p-3 border-t border-neutral-100 dark:border-white/[0.06] flex-shrink-0">
          <NavLink
            to="/intelligence/today"
            className={({ isActive }) =>
              `flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                isActive
                  ? "bg-neutral-900 text-white dark:bg-[#00FF88] dark:text-[#0B0D0F]"
                  : "bg-neutral-900/90 dark:bg-[#00FF88]/10 text-white dark:text-[#00FF88] hover:bg-neutral-900 dark:hover:bg-[#00FF88]/20 border border-transparent dark:border-[#00FF88]/20"
              }`
            }
          >
            <Sparkles className="w-3.5 h-3.5" />
            Abrir Intelligence
          </NavLink>
        </div>
      )}
    </div>
  );

  // ── Mobile top bar ───────────────────────────────────────────────────────────
  const MobileTopBar = () => (
    <div className="lg:hidden flex items-center gap-2 px-4 h-12 bg-white dark:bg-[#0B0D0F] border-b border-neutral-200 dark:border-white/[0.06] flex-shrink-0">
      {logo
        ? <img src={logo} alt="logo" className="w-6 h-6 object-contain rounded flex-shrink-0" data-testid="shell-logo" />
        : <OplyxLogo size={18} wordmark={false} tone="adaptive" />}
      <span className="text-sm font-semibold truncate flex-1 text-neutral-900 dark:text-neutral-100">
        {user.business_name || "OPLYX"}
      </span>
      {onboarding && !onboarding.completed && (
        <button
          type="button"
          onClick={() => setOverlayOpen(true)}
          data-testid="shell-onboarding-badge"
          className="flex items-center gap-1 px-2 py-1 rounded-lg border border-primary/30 bg-primary-muted/20 text-[10px] font-semibold text-primary-muted-foreground"
        >
          <Sparkles className="w-3 h-3" />
          {onboarding.completion_pct || 0}%
        </button>
      )}
      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        data-testid="cmdk-trigger"
        className="w-8 h-8 rounded-lg border border-neutral-200 dark:border-white/[0.06] flex items-center justify-center hover:bg-neutral-100 dark:hover:bg-white/[0.04] transition-colors"
      >
        <Search className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
      </button>
    </div>
  );

  // ── Mobile bottom nav ────────────────────────────────────────────────────────
  const MobileBottomNav = () => {
    const bottomGroups = visibleGroups.filter(g => MOBILE_PRIMARY_IDS.includes(g.id));
    const hasMore = visibleGroups.length > 0;

    return (
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-[#0B0D0F] border-t border-neutral-200 dark:border-white/[0.06] flex items-stretch z-30 pb-safe">
        {bottomGroups.map(group => {
          const isActive = activeGroupId === group.id;
          const Icon = group.icon;
          const firstItem = group.visibleItems[0];
          return (
            <NavLink
              key={group.id}
              to={firstItem?.to ?? "/"}
              data-testid={`mob-tab-${group.id}`}
              className={() =>
                `flex flex-col items-center justify-center gap-1 flex-1 py-2 text-[10px] font-semibold transition-colors relative ${
                  group.isIntel
                    ? isActive ? "text-[#00A855] dark:text-[#00FF88]" : "text-[#00A855]/50 dark:text-[#00FF88]/50"
                    : isActive ? "text-neutral-900 dark:text-neutral-100" : "text-neutral-400 dark:text-neutral-500"
                }`
              }
            >
              <div className="relative">
                <Icon className={`w-5 h-5 ${isActive ? "stroke-[2]" : "stroke-[1.5]"}`} />
                {group.isIntel && <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-[#00FF88] rounded-full border border-white dark:border-[#0B0D0F]" />}
              </div>
              <span>{group.label}</span>
            </NavLink>
          );
        })}
        {hasMore && (
          <button
            type="button"
            onClick={() => setMoreOpen(v => !v)}
            data-testid="mob-tab-mais"
            className={`flex flex-col items-center justify-center gap-1 flex-1 py-2 text-[10px] font-semibold transition-colors ${
              moreOpen ? "text-neutral-900 dark:text-neutral-100" : "text-neutral-400 dark:text-neutral-500"
            }`}
          >
            <MenuIcon className={`w-5 h-5 ${moreOpen ? "stroke-[2]" : "stroke-[1.5]"}`} />
            <span>Mais</span>
          </button>
        )}
      </div>
    );
  };

  // ── Mobile drawer ("Mais") ───────────────────────────────────────────────────
  const MobileDrawer = () => {
    const extraGroups = visibleGroups;
    return (
      <div className="lg:hidden fixed inset-0 z-40 flex flex-col justify-end" data-testid="mob-drawer">
        <div className="absolute inset-0 bg-black/40" onClick={() => setMoreOpen(false)} />
        <div className="relative bg-white dark:bg-[#141618] rounded-t-2xl border-t border-neutral-200 dark:border-white/[0.06] shadow-2xl max-h-[75vh] flex flex-col">
          <div className="w-10 h-1 bg-neutral-300 dark:bg-white/20 rounded-full mx-auto mt-3 mb-1 flex-shrink-0" />
          <div className="overflow-auto px-4 pb-8 pt-3">
            {extraGroups.map(group => (
              <div key={group.id} className="mb-4">
                <div className={`text-[10px] font-bold uppercase tracking-[0.12em] mb-2 ${
                  group.isIntel ? "text-[#00A855] dark:text-[#00FF88]" : "text-neutral-400 dark:text-neutral-600"
                }`}>
                  {group.label}
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {group.visibleItems.map(item => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setMoreOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-medium transition-colors ${
                          isActive
                            ? "bg-neutral-900 dark:bg-[#00FF88] text-white dark:text-[#0B0D0F] border-transparent"
                            : item.danger
                              ? "border-amber-200 dark:border-amber-900/40 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20"
                              : "border-neutral-100 dark:border-white/[0.06] text-neutral-700 dark:text-neutral-300 bg-neutral-50 dark:bg-white/[0.03]"
                        }`
                      }
                    >
                      {item.danger
                        ? <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" strokeWidth={1.5} />
                        : <item.icon className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.5} />}
                      <span className="truncate">{item.label}</span>
                      {lockedFull(item) && <FullBadge />}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}

            {/* Filial + Sair */}
            <div className="border-t border-neutral-100 dark:border-white/[0.06] pt-3 mt-1">
              <BranchSwitcher />
            </div>
            <div className="border-t border-neutral-100 dark:border-white/[0.06] pt-1 mt-1">
              <div className="px-4 py-1.5 text-xs text-neutral-500 truncate">{user.email}</div>
              <OperatorSwitcher />
              <QuickSwitcher />
              <Button
                data-testid="logout-btn"
                variant="ghost"
                className="w-full justify-start text-neutral-600 dark:text-neutral-400 dark:hover:bg-white/[0.04]"
                onClick={logout}
              >
                <LogOut className="w-4 h-4 mr-2" /> Sair
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="h-screen flex flex-col bg-[#FAFAF8] dark:bg-[#0B0D0F] dark:text-neutral-200 overflow-hidden">
      {/* Blocked overlay (pending / suspended / cancelled) */}
      {featuresMeta && ["pending", "suspended", "cancelled"].includes(featuresMeta.tenant_state) && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-neutral-950/95 p-6" data-testid="tenant-blocked-overlay">
          <div className="max-w-md w-full rounded-2xl bg-neutral-900 border border-neutral-800 p-8 text-center text-white space-y-4 shadow-2xl">
            <div className="w-14 h-14 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto">
              <AlertCircle className="w-7 h-7 text-amber-400" />
            </div>
            <h2 className="text-xl font-bold">
              {featuresMeta.tenant_state === "pending" && "Cadastro em análise"}
              {featuresMeta.tenant_state === "suspended" && "Acesso suspenso"}
              {featuresMeta.tenant_state === "cancelled" && "Conta cancelada"}
            </h2>
            <p className="text-sm text-neutral-300">
              {featuresMeta.tenant_state === "pending" && "Seu cadastro está em análise. Em breve nossa equipe entrará em contato."}
              {featuresMeta.tenant_state === "suspended" && "Acesso suspenso. Entre em contato com a OPLYX para regularizar."}
              {featuresMeta.tenant_state === "cancelled" && "Sua conta foi cancelada. Entre em contato com o suporte para reativar."}
            </p>
            <Button onClick={() => window.open(`https://wa.me/${supportWhatsapp}?text=Preciso%20de%20ajuda%20com%20minha%20conta%20OPLYX`, "_blank")} className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-full" data-testid="tenant-blocked-contact">
              Falar com a OPLYX
            </Button>
            <Button variant="ghost" onClick={logout} className="w-full text-neutral-400 hover:bg-neutral-800" data-testid="tenant-blocked-logout">Sair</Button>
          </div>
        </div>
      )}

      {/* Impersonation banner */}
      {localStorage.getItem("oplyx_impersonating") === "1" && (
        <div className="bg-amber-400 text-neutral-950 px-4 py-2 flex items-center justify-between text-sm font-medium flex-shrink-0" data-testid="impersonation-banner">
          <span>⚠ Você está operando como <b>{user.name}</b> ({user.email}) via impersonation admin. Todas as ações são auditadas.</span>
          <Button size="sm" variant="ghost" className="h-7 text-neutral-950 hover:bg-amber-500" onClick={() => { localStorage.removeItem("oplyx_impersonating"); localStorage.clear(); nav("/auth"); }}>
            Encerrar sessão
          </Button>
        </div>
      )}

      {/* Dunning banner */}
      {sub?.payment_status === "past_due" && ["owner","manager"].includes(user.role) && (
        <div
          className={`px-4 py-3 flex items-start justify-between gap-3 text-sm border-b flex-shrink-0 ${
            sub.days_until_block <= 1
              ? "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900/40"
              : "bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-900/40"
          }`}
          data-testid="dunning-banner"
        >
          <div className={`flex items-start gap-2.5 min-w-0 ${sub.days_until_block <= 1 ? "text-red-900 dark:text-red-300" : "text-orange-900 dark:text-orange-300"}`}>
            <AlertCircle className={`w-4 h-4 shrink-0 mt-0.5 ${sub.days_until_block <= 1 ? "text-red-500" : "text-orange-500"}`} />
            <div>
              <span className="font-semibold">Fatura em atraso. </span>
              <span>
                {sub.days_until_block === 0
                  ? "O acesso ao sistema pode ser bloqueado a qualquer momento."
                  : sub.days_until_block === 1
                  ? "O acesso será bloqueado amanhã se a fatura não for regularizada."
                  : `O acesso ao sistema será bloqueado em ${sub.days_until_block} dias se a fatura não for regularizada.`}
              </span>
            </div>
          </div>
          {user.role === "owner" && (
            <Button size="sm" className={`h-7 rounded-full text-white shrink-0 ${sub.days_until_block <= 1 ? "bg-red-600 hover:bg-red-700" : "bg-orange-600 hover:bg-orange-700"}`} onClick={() => nav("/billing")} data-testid="dunning-banner-cta">
              Regularizar
            </Button>
          )}
        </div>
      )}

      {/* Demo banner */}
      {featuresMeta?.tenant_state === "demo" && (
        <div className="bg-amber-500 text-neutral-950 px-4 py-2 flex items-center justify-between gap-3 text-sm shadow-sm flex-shrink-0" data-testid="demo-banner">
          <div className="flex items-center gap-2 font-semibold">
            <AlertCircle className="w-4 h-4" />
            <span>Você está em modo demonstração</span>
            {typeof featuresMeta.demo_days_left === "number" && (
              <span className="opacity-80 font-medium">· {featuresMeta.demo_days_left} {featuresMeta.demo_days_left === 1 ? "dia restante" : "dias restantes"}</span>
            )}
          </div>
          <Button size="sm" className="h-7 bg-neutral-950 hover:bg-neutral-800 text-amber-400 rounded-full" onClick={() => window.open(`https://wa.me/${supportWhatsapp}?text=Quero%20ativar%20minha%20conta%20OPLYX`, "_blank")} data-testid="demo-banner-cta">
            Ativar plano
          </Button>
        </div>
      )}

      {/* Trial banner */}
      {showTrialBanner && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900/40 px-4 py-2 flex items-center justify-between gap-3 text-sm flex-shrink-0">
          <div className="flex items-center gap-2 text-amber-900 dark:text-amber-300"><AlertCircle className="w-4 h-4" />Seu teste termina em {sub.days_left} dia(s).</div>
          <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-7" onClick={() => nav("/billing")} data-testid="trial-cta">Assinar agora</Button>
        </div>
      )}

      {/* 1ª mensalidade ainda não paga (cadastro sem trial): datas sempre à vista */}
      {sub?.first_charge?.first_due && sub?.payment_status !== "past_due" && user.role === "owner" && (
        <div className="bg-emerald-50 dark:bg-emerald-950/30 border-b border-emerald-200 dark:border-emerald-900/40 px-4 py-2 flex items-center justify-between gap-3 text-sm flex-shrink-0" data-testid="first-charge-banner">
          <div className="text-emerald-900 dark:text-emerald-300">
            Sua 1ª mensalidade vence em <b>{new Date(`${sub.first_charge.first_due}T12:00:00`).toLocaleDateString("pt-BR")}</b>
            {sub.first_charge.method === "CREDIT_CARD" ? " no cartão cadastrado" : sub.first_charge.method === "PIX" ? " por Pix" : sub.first_charge.method === "BOLETO" ? " por boleto" : ""}.
            {sub.first_charge.anchor_day && <> Depois, todo dia <b>{sub.first_charge.anchor_day}</b> de cada mês.</>}
          </div>
          <Button size="sm" variant="ghost" className="rounded-full h-7 text-emerald-800 dark:text-emerald-300" onClick={() => nav("/billing")} data-testid="first-charge-banner-cta">Ver detalhes</Button>
        </div>
      )}

      {/* Certificate expiry banner */}
      {certAlert && (
        <div
          className={`px-4 py-2 flex items-center justify-between gap-3 text-sm flex-shrink-0 ${
            certAlert.status === "expired"
              ? "bg-red-600 text-white border-b border-red-700"
              : "bg-amber-500 text-neutral-950 border-b border-amber-600"
          }`}
          data-testid="cert-expiry-banner"
        >
          <div className="flex items-center gap-2 font-semibold min-w-0">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span className="truncate">
              {certAlert.status === "expired"
                ? "Certificado vencido. Emissão de NFC-e bloqueada. Renove agora."
                : `Certificado digital vence em ${certAlert.days_remaining} dia(s). Renove agora.`}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" className={`h-7 rounded-full ${certAlert.status === "expired" ? "bg-white text-red-700 hover:bg-red-50" : "bg-neutral-950 hover:bg-neutral-800 text-amber-400"}`} onClick={() => nav("/fiscal/configuracao")} data-testid="cert-expiry-banner-cta">
              Renovar certificado
            </Button>
            <button type="button" onClick={() => setCertAlert(null)} title="Fechar" data-testid="cert-expiry-banner-dismiss" className="opacity-70 hover:opacity-100 transition-opacity">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Desktop deck nav */}
      {!posStandalone && <DeckTopBar />}
      {!posStandalone && <DeckSubNav />}

      {/* Mobile top bar */}
      {!posStandalone && <MobileTopBar />}

      {/* Main content row */}
      <div className="flex-1 flex overflow-hidden">
        <main
          ref={mainScrollRef}
          className="flex-1 overflow-auto p-4 md:p-8 pb-24 lg:pb-8"
        >
          {showBackToIntel && (
            <button
              type="button"
              onClick={() => nav(fromIntel)}
              data-testid="back-to-intelligence"
              className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-[#00FF88]/30 bg-[#00FF88]/[0.06] px-3 py-1 text-xs font-medium text-[#00A855] dark:text-[#00FF88] hover:bg-[#00FF88]/[0.12] transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Voltar ao Intelligence
            </button>
          )}
          <RouteErrorBoundary>
            <Outlet />
          </RouteErrorBoundary>
        </main>

        {/* Intelligence panel */}
        {!posStandalone && intelOpen && <IntelPanel />}
      </div>

      {/* Mobile bottom nav + drawer */}
      {!posStandalone && <MobileBottomNav />}
      {!posStandalone && moreOpen && <MobileDrawer />}

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} navGroups={visibleGroups} />

      {overlayOpen && location.pathname !== "/welcome" && (
        <OnboardingOverlay
          open={overlayOpen}
          onClose={() => {
            sessionStorage.setItem("onboarding_overlay_shown", "1");
            setOverlayOpen(false);
          }}
          onComplete={() => {
            setOnboarding(prev => prev ? { ...prev, completed: true } : null);
            setOverlayOpen(false);
          }}
        />
      )}
    </div>
  );
}

function RouteErrorBoundary({ children }) {
  const location = useLocation();
  // A chave é SÓ o pathname. Com `location.search` junto, qualquer mudança de
  // query string trocava a chave e o React desmontava e remontava a página
  // inteira — todo o estado da tela ia junto.
  //
  // Foi assim que o fechamento de comanda pelo caixa perdeu a NFC-e: o PDV
  // grava a venda, guarda o resultado em `lastSale` (o diálogo pós-venda, com
  // o status da nota e o botão de imprimir) e na linha seguinte chama
  // `exitTabMode()`, que limpa o `?tab=<id>` da URL. A limpeza trocava a chave,
  // o <POS /> remontava zerado e o diálogo nunca chegava a aparecer. Venda
  // direta no PDV não mexe na URL — por isso só o caminho da comanda quebrava.
  //
  // O preço de tirar o `search`: uma página que quebrou continua quebrada se o
  // usuário só mudar a query string. Sair da rota e voltar reseta — barato
  // perto de perder o estado da tela a cada filtro.
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}
